import { Link } from "react-router-dom";
import { Play, Pause, Clock, ArrowRight, Rss } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import EpisodeCard from "@/components/EpisodeCard";
import { usePlayer } from "@/context/PlayerContext";
import { show, episodes, getLatest, formatDate, formatDuration } from "@/lib/podcast";
import { siteConfig } from "@/config/siteConfig";

export default function Home() {
  const { playEpisode, toggle, isActive, isPlaying } = usePlayer();
  const latest = getLatest();
  const recent = episodes.slice(1, 7);
  const active = latest && isActive(latest);

  return (
    <div data-testid="home-page">
      <Seo
        title={show.title}
        description={show.description}
        image={show.image}
        type="website"
      />

      {/* Hero */}
      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#9a6700] mb-5">
                <span className="h-2 w-2 rounded-full bg-[#f5b61c] animate-pulse" />
                {show.categories?.join(" · ")}
              </span>
              <h1 className="font-poster uppercase text-5xl sm:text-6xl lg:text-7xl text-[#0b1f4d] leading-[0.95] tracking-tight">
                Conversations
                <br />
                Along the Road of
                <br />
                <span className="text-[#b27300]">West Virginia Education.</span>
              </h1>
              <p className="mt-6 text-lg text-[#374566] leading-relaxed max-w-xl">
                {show.description}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/episodes"
                  data-testid="hero-browse-episodes"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-semibold uppercase tracking-wide px-6 py-3 transition-colors"
                >
                  Browse Episodes <ArrowRight size={18} />
                </Link>
                <Link
                  to="/follow"
                  data-testid="hero-follow-link"
                  className="inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/20 hover:border-[#f5b61c] text-[#0b1f4d] font-semibold uppercase tracking-wide px-6 py-3 transition-colors"
                >
                  Follow the show
                </Link>
              </div>
            </div>

            {/* Featured latest episode */}
            {latest && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-5"
              >
                <div
                  data-testid="hero-featured-episode"
                  className="bg-white rounded-lg border border-[#0b1f4d]/10 shadow-xl shadow-[#0b1f4d]/10 overflow-hidden"
                >
                  <div className="relative aspect-square">
                    <img
                      src={latest.image}
                      alt={latest.title}
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-widest bg-[#f5b61c] text-[#0a1630] px-2.5 py-1 rounded-sm">
                      Latest Episode
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest text-[#66718d] mb-2">
                      <span>{formatDate(latest.pubDate)}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} /> {formatDuration(latest.durationSeconds)}
                      </span>
                    </div>
                    <Link to={`/episodes/${latest.slug}`}>
                      <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] leading-snug hover:text-[#9a6700] transition-colors">
                        {latest.title}
                      </h2>
                    </Link>
                    <p className="mt-3 text-sm text-[#4d5a78] leading-relaxed line-clamp-3">
                      {latest.excerpt}
                    </p>
                    <div className="mt-5 flex items-center gap-3">
                      <button
                        data-testid="hero-play-button"
                        onClick={() => (active ? toggle() : playEpisode(latest))}
                        className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-semibold uppercase tracking-wide px-5 py-2.5 transition-colors"
                      >
                        {active && isPlaying ? (
                          <>
                            <Pause size={17} /> Pause
                          </>
                        ) : (
                          <>
                            <Play size={17} /> Play now
                          </>
                        )}
                      </button>
                      <Link
                        to={`/episodes/${latest.slug}`}
                        className="text-sm font-semibold text-[#374566] hover:text-[#9a6700] transition-colors"
                      >
                        Show notes →
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Recent episodes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
              The Feed
            </span>
            <h2 className="font-display uppercase text-3xl sm:text-4xl font-semibold tracking-tight text-[#0b1f4d] mt-1">
              Recent episodes
            </h2>
          </div>
          <Link
            to="/episodes"
            data-testid="home-all-episodes-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#374566] hover:text-[#9a6700] transition-colors"
          >
            All episodes <ArrowRight size={16} />
          </Link>
        </div>

        {recent.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recent.map((ep) => (
              <EpisodeCard key={ep.id} ep={ep} />
            ))}
          </div>
        ) : (
          <p className="text-[#66718d]">
            More episodes are on the way — new ones appear here automatically as
            they publish.
          </p>
        )}
      </section>

      {/* Host spotlight */}
      {show.person && (
        <section className="border-y border-[#0b1f4d]/10 bg-[#f3ecdc]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <div className="flex flex-col sm:flex-row items-start gap-8">
              <img
                src={show.person.img}
                alt={show.person.name}
                className="h-28 w-28 rounded-lg object-cover ring-1 ring-[#0b1f4d]/10"
              />
              <div className="max-w-2xl">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
                  Your host
                </span>
                <h3 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] mt-1">
                  {show.person.name}
                </h3>
                <p className="mt-3 text-[#374566] leading-relaxed">
                  {siteConfig.about.host.bio[0]}
                </p>
                <Link
                  to="/about"
                  data-testid="home-about-link"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#374566] hover:text-[#9a6700] transition-colors"
                >
                  About the show <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Subscribe bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="hero-bg rounded-lg border border-[#0b1f4d]/10 px-6 sm:px-10 py-10 sm:py-12 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="font-display uppercase text-2xl sm:text-3xl font-semibold text-[#0b1f4d]">
              Never miss an episode
            </h3>
            <p className="mt-2 text-[#374566] max-w-lg">
              Follow WVEdChat in your favorite podcast app and new
              episodes show up automatically.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/follow"
              data-testid="subscribe-follow-btn"
              className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-bold uppercase tracking-wide px-6 py-3 transition-colors"
            >
              Choose your app
            </Link>
            <a
              href={siteConfig.feedUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="subscribe-rss-btn"
              className="inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/25 hover:bg-[#0b1f4d]/5 text-[#0b1f4d] font-semibold uppercase tracking-wide px-5 py-3 transition-colors"
            >
              <Rss size={17} /> RSS
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
